import React, { useState, useEffect, ReactNode } from 'react';
import { GathernTokenExpiredModal } from '../../pages/PropertiesManagement/Linking/Components/GathernTokenExpiredModal';
import { useGetUserDetail } from '../../services/home/getUserDetail';

interface GathernTokenGuardProps {
    children: ReactNode;
}

export const GathernTokenGuard: React.FC<GathernTokenGuardProps> = ({ children }) => {
    const [isTokenExpired, setIsTokenExpired] = useState(false);
    const { refetch } = useGetUserDetail({ enabled: false });

    useEffect(() => {
        // Check token status on mount and when localStorage changes
        const checkTokenStatus = () => {
            const gathernTokenExpired = localStorage.getItem('gathernTokenExpired');
            
            // Token is expired ONLY if gathernTokenExpired is explicitly set to "true"
            // We don't check gathernToken value - users may not have connected Gathern yet
            const isExpired = gathernTokenExpired === "true";
            
            setIsTokenExpired(isExpired);
        };

        // Initial check
        checkTokenStatus();

        // Listen for localStorage changes (in case it changes from another tab or component)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'gathernToken' || e.key === 'gathernTokenExpired') {
                checkTokenStatus();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Also check periodically (every 30 seconds) in case localStorage changes without event
        const intervalId = setInterval(checkTokenStatus, 30000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(intervalId);
        };
    }, []);

    const handleModalClose = () => {
        // Modal should not be closeable by user - only through successful OTP verification
        // This function is required by the modal props but won't do anything
    };

    const handleSuccess = async () => {
        // After successful OTP verification, refetch user details to update localStorage
        await refetch();
        
        // Re-check token status
        const gathernTokenExpired = localStorage.getItem('gathernTokenExpired');
        setIsTokenExpired(gathernTokenExpired === "true");
    };

    return (
        <>
            {children}
            <GathernTokenExpiredModal
                open={isTokenExpired}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
            />
        </>
    );
};
